import './StepIndicator.css';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { menuLogout } from '../../app/http-common';
import { setActiveTab, setCurrentStep } from './slice';
import { useCanEdit, useIsAdmin, useUserInfo } from '../../hooks/useFrontegg';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { useOnboarding } from '../../contexts/OnboardingContext';

function StepIndicator({ currentStep, onStepClick, isNextStepEnable, onSmartCopy, onPublishCms, canCreateBrief = true, hideSteps = false, sidebarOffset = 0, hasCopiedDraft = false, isPrimaryActionLoading = false }) {
    const canEdit = useCanEdit();
    const isAdmin = useIsAdmin();
    const location = useLocation();
    const navigate = useNavigate();
    const isOnBucketPageNew = location.pathname.startsWith('/publisher-buckets-new');
    // Active while the Trending link's own parameter is present, since there is
    // no /signals page any more.
    //
    // Deliberately NOT read from the redux store: this component is rendered
    // bare in StepIndicator.test.jsx with no Provider, so a useSelector here
    // breaks four tests that have nothing to do with Signals. The cost is that
    // toggling the sidebar checkbox does not light this link, which is
    // cosmetic. The sidebar checkbox is the control; this is a shortcut to it.
    const isOnSignalsPage = new URLSearchParams(location.search).get('trending') === '1';
    const userInfo = useUserInfo();
    const { canReset: canResetOnboarding, resetOnboarding } = useOnboarding();
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const steps = ['Stories', 'Articles', 'Claims', 'Draft'];

    const getInitials = () => {
        if (!userInfo?.name) return '?';
        return userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleStepClick = async (index) => {
        if (!canCreateBrief && index > 1) return;
        if (index <= currentStep + 1 && onStepClick && (index <= currentStep || isNextStepEnable())) {
            await onStepClick(index);
        }
        if ((index === 3 && currentStep === 1) && isNextStepEnable()) {
            await onStepClick(index);
        }
    };

    const handleNextClick = async () => {
        if (isNextStepEnable() && currentStep <= steps.length - 1) {
            await onStepClick(currentStep + 1);
        }
    };

    const handleBackClick = async () => {
        if (currentStep > 0) {
            await onStepClick(currentStep - 1);
        }
    };

    useEffect(() => {
        if (!menuOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [menuOpen]);

    return (
        <div className="step-indicator-container">
            <button className="logo-button" onClick={() => { window.location.href = '/'; }}>
                <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="company-logo" />
            </button>
            <span className="header-tagline">The relevance engine</span>

            {!hideSteps && (
                <div className="steps-container">
                    {steps.map((step, index) => (
                        <div key={step} className="step-wrapper">
                            <button
                                className={`step ${index === currentStep ? 'active' : ''} ${
                                    (!canCreateBrief && index > 1) ? 'disabled readonly' :
                                    (!isNextStepEnable() && index > currentStep) ? 'disabled' : ''
                                }`}
                                onClick={() => handleStepClick(index)}
                                disabled={(!canCreateBrief && index > 1) || (!isNextStepEnable() && index > currentStep)}
                                title={!canCreateBrief && index > 1 ? 'Readonly users cannot create drafts' : ''}
                            >
                                <span className="step-title">{step}</span>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="header-right-cluster">
                {/* The legacy /publisher-buckets entry point is hidden: the
                    redesigned screen is now the only one anybody should be
                    reaching from the header. The ROUTE is deliberately left
                    mounted in App.js so existing bookmarks and any links
                    already sent out keep resolving — this removes the way IN,
                    not the page itself.
                    With one bucket destination left, the "✦" that distinguished
                    the new one from the old has nothing to distinguish. */}
                {canEdit && (
                    <a
                        href="/publisher-buckets-new"
                        className={`bucket-nav-link ${isOnBucketPageNew ? 'active' : ''}`}
                        title="Publisher Buckets"
                    >
                        <InventoryOutlinedIcon style={{ fontSize: 16 }} /> Buckets
                    </a>
                )}
                {/* Goes to the FEED with the Trending filter on, not to a
                    separate screen. The standalone page showed a Signal as a
                    bare line of text: no article count, no preview, no way to
                    put it in a bucket. A Signal is a story, so it belongs in
                    the story list, marked. */}
                {canEdit && (
                    <a
                        href="/?trending=1"
                        className={`bucket-nav-link ${isOnSignalsPage ? 'active' : ''}`}
                        title="Trending stories, the ones selected as Signals"
                    >
                        <BoltOutlinedIcon style={{ fontSize: 16 }} /> Trending
                    </a>
                )}
                <div className="user-menu-wrap" ref={menuRef}>
                    <button
                        type="button"
                        className="user-menu-trigger"
                        onClick={() => setMenuOpen((prev) => !prev)}
                    >
                        <div className="header-avatar" title={userInfo?.name || 'User'}>
                            {getInitials()}
                        </div>
                        <span className="user-menu-name">{userInfo?.name || 'User'}</span>
                        <span className="user-menu-caret">▾</span>
                    </button>
                    {menuOpen && (
                        <div className="user-menu-dropdown">
                            {canCreateBrief && (
                                <button
                                    type="button"
                                    className="user-menu-item"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        navigate('/my-drafts');
                                    }}
                                >
                                    My Drafts
</button>
                            )}
                            {canResetOnboarding && (
                                <button
                                    type="button"
                                    className="user-menu-item"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        resetOnboarding();
                                        // Return the wizard to the first step so the guide restarts
                                        // from "Find a story" (the persisted wizard state would
                                        // otherwise restore the last position, e.g. Claims)
                                        dispatch(setCurrentStep(0));
                                        dispatch(setActiveTab('searchStories'));
                                        if (location.pathname !== '/') {
                                            navigate('/');
                                        }
                                    }}
                                >
                                    Restart onboarding
                                </button>
                            )}
                            <button
                                type="button"
                                className="user-menu-item"
                                onClick={() => {
                                    setMenuOpen(false);
                                    menuLogout();
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {!hideSteps && currentStep > 0 && (canCreateBrief || currentStep <= 1) && (
                <>
                    <div className="floating-buttons-backdrop" />
                    <div className="floating-buttons">
                        <button className="back-button" onClick={handleBackClick} disabled={currentStep === 0}>
                            ← Back
                        </button>
                        {canCreateBrief && (
                            currentStep === 3 ? (
                                <>
                                    <button
                                        className="next-button"
                                        data-tour-id="copy-draft-button"
                                        onClick={onSmartCopy}
                                        disabled={!isNextStepEnable()}
                                    >
                                        Copy Draft
                                    </button>
                                    {isAdmin && onPublishCms && (
                                        <button
                                            className="next-button"
                                            onClick={onPublishCms}
                                            disabled={!isNextStepEnable()}
                                            style={{ background: '#00AEEF' }}
                                        >
                                            Publish to CMS
                                        </button>
                                    )}
                                    {hasCopiedDraft && (
                                        <button
                                            className="back-button"
                                            onClick={() => onStepClick(0)}
                                        >
                                            ← Back to stories
                                        </button>
                                    )}
                                </>
                            ) : (
                                <button
                                    className="next-button"
                                    data-tour-id="next-button"
                                    onClick={handleNextClick}
                                    disabled={isPrimaryActionLoading || currentStep === 0 || !isNextStepEnable() || (!canCreateBrief && currentStep >= 1)}
                                >
                                    {currentStep === 2 ? (isPrimaryActionLoading ? 'Generating...' : 'Generate draft') : 'Next →'}
                                </button>
                            )
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default StepIndicator;
